/**
 * "Receive delivery" side panel (22 #5) — a bottom sheet on phones. Opened from an LPO it starts filled in:
 * the LPO's project and supplier, and every line still to come with a stepper set to what remains (an
 * amber note when more arrives than was still expected). Without an LPO the items are picked by hand.
 * The supplier is always asked for (prefilled from the LPO, editable, suggestions from known suppliers) so no
 * delivery note is saved without one.
 * Destination uses the same words as the lists: Warehouse / Direct to site. Saves one delivery note
 * (POST /delivery-notes) — the shell's toast offers Undo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HardHat, Minus, Plus, TriangleAlert, Truck, Warehouse, X } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber, toDateInputValue, toNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SheetClose } from '@/components/ui/sheet';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { notify } from '@/components/ui/notify';
import { SearchSelect } from './SearchSelect';
import { MissingText, Segmented, plural } from './SfUi';
import { receiveLinesFor } from './procurementModel';
import { PHONE_SHEET_CLASS, searchCatalogParts } from './LpoFormPanel';

const fieldLabel = 'text-[12px] font-semibold text-ink-2';
const DEFAULT_STORE = 'Abu Dhabi Store';
let seq = 0;
const manualLine = () => { seq += 1; return { key: `manual-${seq}`, part_number: '', description: '', qty: '', fromLpo: false }; };

function Stepper({ value, onChange, label, testId, warn }) {
  const number = toNumber(value) ?? 0;
  const step = (delta) => onChange(String(Math.max(0, Math.round((number + delta) * 1000) / 1000)));
  return (
    <div className={cn('inline-flex h-11 shrink-0 items-center overflow-hidden rounded-control border bg-surface sm:h-9', warn ? 'border-[color:var(--status-amber)]' : 'border-line')}>
      <button type="button" onClick={() => step(-1)} disabled={number <= 0} aria-label={`One less ${label}`} className="grid h-full w-11 place-items-center text-ink-3 hover:bg-surface-hover disabled:opacity-40 sm:w-8">
        <Minus className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={`Quantity received, ${label}`}
        data-testid={testId}
        className="h-full w-14 border-x border-line-2 bg-transparent text-center text-[13.5px] font-semibold tabular-nums text-ink outline-none focus-visible:bg-surface-hover [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button type="button" onClick={() => step(1)} aria-label={`One more ${label}`} className="grid h-full w-11 place-items-center text-ink-3 hover:bg-surface-hover sm:w-8">
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

function initialState({ lpo, userName }) {
  const fromLpo = lpo ? receiveLinesFor(lpo).map((line) => ({ ...line, qty: String(line.remaining) })) : [];
  return {
    lpoId: lpo?.id || '',
    projectId: lpo?.project_name ? (lpo.project_id || '') : '',
    supplier: String(lpo?.supplier || '').trim(),
    dnNumber: '',
    deliveryDate: toDateInputValue(new Date()) || '',
    receivedBy: userName || '',
    deliveryType: 'to_stock',
    notes: '',
    lines: lpo ? fromLpo : [manualLine()],
  };
}

const snapshot = (state) => JSON.stringify({ ...state, lines: state.lines.map(({ key, ...rest }) => rest) });

/**
 * props: open, onOpenChange, lpos (from GET /lpos, with delivered / remaining per line), projects,
 * suppliers (names for the supplier suggestions), lpoId (start from this LPO), userName (Received by), onCreated(dn).
 */
export function ReceiveDeliveryPanel({ open, onOpenChange, lpos = [], projects = [], suppliers = [], lpoId: startLpoId, userName, onCreated }) {
  const lpoById = useMemo(() => new Map(lpos.map((lpo) => [lpo.id, lpo])), [lpos]);
  const [state, setState] = useState(() => initialState({ lpo: lpoById.get(startLpoId), userName }));
  const [initial, setInitial] = useState(() => snapshot(state));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState({});
  const numberRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const next = initialState({ lpo: lpoById.get(startLpoId), userName });
    setState(next);
    setInitial(snapshot(next));
    setError('');
    setInvalid({});
    const touch = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && Boolean(window.matchMedia('(pointer: coarse)')?.matches);
    const timer = touch ? null : setTimeout(() => numberRef.current?.focus(), 60);
    return () => clearTimeout(timer);
    // Only when the panel opens (or is pointed at another LPO) — not on every list refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startLpoId]);

  const lpo = lpoById.get(state.lpoId) || null;
  const fullyDelivered = lpo ? (lpo.line_items || []).length - receiveLinesFor(lpo).length : 0;
  const dirty = snapshot(state) !== initial;
  const set = (patch) => setState((prev) => ({ ...prev, ...patch }));
  const setLine = (key, patch) => setState((prev) => ({ ...prev, lines: prev.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)) }));

  const chooseLpo = (id) => {
    const next = lpoById.get(id);
    setState((prev) => ({
      ...prev,
      lpoId: id || '',
      projectId: next?.project_name ? (next.project_id || prev.projectId) : prev.projectId,
      // The LPO's supplier when it has one; otherwise keep what was typed.
      supplier: String(next?.supplier || '').trim() || prev.supplier,
      lines: next ? receiveLinesFor(next).map((line) => ({ ...line, qty: String(line.remaining) })) : [manualLine()],
    }));
  };

  const lpoOptions = useMemo(() => [
    { value: '', label: 'No LPO — enter the items by hand' },
    ...lpos
      .map((item) => {
        const toCome = receiveLinesFor(item).reduce((sum, line) => sum + line.remaining, 0);
        return { value: item.id, label: item.lpo_number || 'LPO', sub: `${item.supplier || 'Supplier missing'} · ${toCome ? `${formatNumber(toCome)} pcs to come` : 'fully delivered'}`, toCome };
      })
      .sort((a, b) => (b.toCome > 0) - (a.toCome > 0) || a.label.localeCompare(b.label, undefined, { numeric: true })),
  ], [lpos]);
  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name || 'Unnamed project', sub: [project.client, project.system_type].filter(Boolean).join(' · ') }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [projects]
  );

  const counted = state.lines.filter((line) => line.part_number && (toNumber(line.qty) || 0) > 0);
  const pieces = counted.reduce((sum, line) => sum + toNumber(line.qty), 0);

  const save = useCallback(async () => {
    setError('');
    const problems = { number: !state.dnNumber.trim(), project: !state.projectId, supplier: !state.supplier.trim() };
    setInvalid(problems);
    try {
      const missing = [problems.number && 'the DN number', problems.project && 'the project', problems.supplier && 'the supplier'].filter(Boolean);
      if (missing.length > 1) throw new Error(`Fill in ${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`);
      if (problems.number) throw new Error('Enter the delivery note (DN) number');
      if (problems.project) throw new Error('Pick the project this delivery is for');
      if (problems.supplier) throw new Error('Enter the supplier this delivery came from');
      const items = [];
      state.lines.forEach((line) => {
        const part = String(line.part_number || '').trim();
        const raw = String(line.qty ?? '').trim();
        if (!part && !raw) return;
        if (!part) throw new Error('Pick a part number for every item, or remove the empty row');
        const qty = raw ? toNumber(raw) : 0;
        if (qty === null || qty < 0) throw new Error(`${part}: the quantity must be 0 or more`);
        if (qty > 0) items.push({ part_number: part, delivered_qty: qty, description: line.description || '' });
      });
      if (!items.length) throw new Error('Enter what arrived — at least one item with a quantity');
      setSaving(true);
      const payload = {
        project_id: state.projectId,
        lpo_id: state.lpoId || '',
        dn_number: state.dnNumber.trim(),
        supplier: state.supplier.trim(),
        delivery_date: state.deliveryDate || '',
        received_by: state.receivedBy.trim(),
        notes: state.notes.trim(),
        delivery_type: state.deliveryType,
        line_items: items,
      };
      const res = await api.post('/delivery-notes', payload);
      const total = items.reduce((sum, item) => sum + item.delivered_qty, 0);
      notify.success(`Delivery ${payload.dn_number} recorded`, {
        description: `${plural(items.length, 'item')} · ${formatNumber(total)} pcs${lpo ? ` on ${lpo.lpo_number}` : ''}`,
      });
      onCreated?.(res.data);
    } catch (err) {
      const message = getErrorMessage(err, 'Could not record the delivery');
      setError(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  }, [state, lpo, onCreated]);

  const submit = async () => {
    try {
      await save();
      onOpenChange(false);
    } catch {
      // shown in the footer
    }
  };

  const footer = (
    <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px] sm:py-3.5">
      <div className="min-w-0 flex-1 text-[12.5px]">
        {error ? (
          <span className="flex min-w-0 items-start gap-1.5 font-semibold text-status-red-ink" role="alert" data-testid="dn-form-error">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">{error}</span>
          </span>
        ) : (
          <span className="text-ink-3" data-testid="dn-form-summary">{plural(counted.length, 'item')} · <b className="font-semibold text-ink">{formatNumber(pieces)} pcs</b></span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <SheetClose asChild>
          <Button type="button" variant="ghost" disabled={saving}>Cancel</Button>
        </SheetClose>
        <Button type="button" onClick={submit} disabled={saving} data-testid="submit-dn-btn">
          {saving ? 'Saving…' : 'Record delivery'}
        </Button>
      </div>
    </footer>
  );

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title="Receive delivery"
      subtitle={lpo ? `${lpo.lpo_number}${lpo.supplier ? ` · ${lpo.supplier}` : ''}` : 'Record what arrived — into the warehouse or straight to site.'}
      icon={Truck}
      width="md"
      dirty={dirty}
      onSave={save}
      saving={saving}
      error={error || ''}
      footer={footer}
      className={PHONE_SHEET_CLASS}
      testId="dn-form"
    >
      <SidePanelSection title="Delivery note">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label className={fieldLabel} htmlFor="dn-lpo">LPO</Label>
            <SearchSelect
              id="dn-lpo"
              value={state.lpoId}
              valueLabel={lpo ? lpo.lpo_number : ''}
              onChange={chooseLpo}
              options={lpoOptions}
              placeholder="No LPO — enter the items by hand"
              searchPlaceholder="Search LPO number or supplier"
              mono={Boolean(lpo)}
              testId="dn-lpo-select"
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className={fieldLabel} htmlFor="dn-project">Project *</Label>
            <SearchSelect
              id="dn-project"
              value={state.projectId}
              onChange={(value) => { set({ projectId: value }); setInvalid((prev) => ({ ...prev, project: false })); }}
              options={projectOptions}
              placeholder="Pick the project"
              searchPlaceholder="Search projects"
              invalid={invalid.project}
              testId="dn-project-select"
            />
            {lpo && !lpo.project_name ? (
              <MissingText className="block">{lpo.lpo_number} has no project linked — pick where this delivery belongs.</MissingText>
            ) : null}
          </div>
          <div className="min-w-0 space-y-1">
            <Label className={fieldLabel} htmlFor="dn-number">DN number *</Label>
            <Input
              ref={numberRef}
              id="dn-number"
              value={state.dnNumber}
              onChange={(e) => { set({ dnNumber: e.target.value }); setInvalid((prev) => ({ ...prev, number: false })); }}
              aria-invalid={invalid.number || undefined}
              className={cn('font-mono placeholder:font-sans placeholder:text-ink-4', invalid.number && 'border-[color:var(--status-red)]')}
              placeholder="e.g. DN-0142"
              data-testid="dn-number-input"
            />
          </div>
          <div className="min-w-0 space-y-1">
            <Label className={fieldLabel} htmlFor="dn-date">Delivery date</Label>
            <Input id="dn-date" type="date" value={state.deliveryDate} onChange={(e) => set({ deliveryDate: e.target.value })} />
          </div>
          <div className="min-w-0 space-y-1">
            <Label className={fieldLabel} htmlFor="dn-supplier">Supplier *</Label>
            <Input
              id="dn-supplier"
              list="dn-supplier-options"
              value={state.supplier}
              onChange={(e) => { set({ supplier: e.target.value }); setInvalid((prev) => ({ ...prev, supplier: false })); }}
              aria-invalid={invalid.supplier || undefined}
              className={cn(invalid.supplier && 'border-[color:var(--status-red)]')}
              placeholder="Who delivered it"
              data-testid="dn-supplier-input"
            />
            <datalist id="dn-supplier-options">
              {[...new Set(suppliers.map((name) => String(name || '').trim()).filter(Boolean))].sort().map((name) => <option key={name} value={name} />)}
            </datalist>
          </div>
          <div className="min-w-0 space-y-1">
            <Label className={fieldLabel} htmlFor="dn-received-by">Received by</Label>
            <Input id="dn-received-by" value={state.receivedBy} onChange={(e) => set({ receivedBy: e.target.value })} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <span className={fieldLabel} id="dn-destination-label">Destination</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <Segmented
                ariaLabel="Destination"
                value={state.deliveryType}
                onChange={(value) => set({ deliveryType: value })}
                options={[
                  { key: 'to_stock', label: 'Warehouse', testId: 'dn-destination-warehouse' },
                  { key: 'to_site', label: 'Direct to site', testId: 'dn-destination-site' },
                ]}
              />
              <span className="flex items-center gap-1.5 text-[12px] text-ink-3">
                {state.deliveryType === 'to_site'
                  ? <><HardHat className="h-3.5 w-3.5" aria-hidden="true" />Straight to the project site — stock does not change</>
                  : <><Warehouse className="h-3.5 w-3.5" aria-hidden="true" />Adds the items to stock in {DEFAULT_STORE}</>}
              </span>
            </div>
          </div>
        </div>
      </SidePanelSection>

      <SidePanelSection
        title={lpo ? `Items still to come · ${formatNumber(state.lines.length)}` : 'Items'}
        description={lpo ? `Each line starts at what is still expected — change it to what actually arrived.${fullyDelivered > 0 ? ` ${plural(fullyDelivered, 'line')} already fully delivered.` : ''}` : undefined}
        actions={lpo ? null : <Button type="button" variant="ghost" size="sm" onClick={() => set({ lines: [...state.lines, manualLine()] })}><Plus aria-hidden="true" /> Add item</Button>}
      >
        {lpo && !state.lines.length ? (
          <p className="rounded-xl bg-surface-2 px-3.5 py-3 text-[12.5px] text-ink-3">Everything on {lpo.lpo_number} has been delivered.</p>
        ) : null}
        <ul className="divide-y divide-line-2 rounded-xl border border-line-2" data-testid="dn-form-lines">
          {state.lines.map((line, index) => {
            const qty = toNumber(line.qty) ?? 0;
            const over = line.fromLpo && qty > line.remaining;
            const skipped = line.fromLpo && qty === 0;
            return (
              <li key={line.key} className={cn('px-3 py-2.5', skipped && 'bg-surface-2')} data-testid={`dn-line-${index}`}>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    {line.fromLpo ? (
                      <>
                        <span className="block font-mono text-[12.5px] font-semibold text-ink">{line.part_number}</span>
                        <span className="block truncate text-[12px] text-ink-3">
                          {line.description ? `${line.description} · ` : ''}{formatNumber(line.remaining)} still to come
                        </span>
                      </>
                    ) : (
                      <SearchSelect
                        value={line.part_number}
                        onChange={(value, option) => setLine(line.key, { part_number: value, description: option?.description || '' })}
                        loadOptions={searchCatalogParts}
                        allowCustom
                        mono
                        placeholder="Part number"
                        searchPlaceholder="Search part no. or description"
                        aria-label={`Part number, item ${index + 1}`}
                        testId={`dn-line-part-${index}`}
                      />
                    )}
                  </div>
                  <Stepper value={line.qty} onChange={(value) => setLine(line.key, { qty: value })} label={line.part_number || `item ${index + 1}`} warn={over} testId={`dn-line-qty-${index}`} />
                  {!line.fromLpo ? (
                    <Button type="button" variant="ghost" size="icon-xs" onClick={() => set({ lines: state.lines.length > 1 ? state.lines.filter((item) => item.key !== line.key) : [manualLine()] })} aria-label={`Remove item ${index + 1}`} className="text-ink-3">
                      <X aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
                {over ? (
                  <p className="mt-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-status-amber-ink" role="status" data-testid={`dn-line-over-${index}`}>
                    <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {formatNumber(qty - line.remaining)} more than the {formatNumber(line.remaining)} still expected on this LPO
                  </p>
                ) : skipped ? (
                  <p className="mt-1 text-[11.5px] text-ink-3">Not in this delivery</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </SidePanelSection>

      <SidePanelSection title="Notes" collapsible defaultOpen={false}>
        <Input value={state.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="e.g. 2 boxes damaged, photos sent to supplier" aria-label="Notes" />
      </SidePanelSection>
    </SidePanel>
  );
}
